import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

// ✅ Custom Date Input (Responsive + พ.ศ.)
const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => {
  const displayValue = (() => {
    if (!value) return "";
    const [dd, mm, yyyy] = value.split("-");
    const thYear = parseInt(yyyy) + 543;
    return `${dd}/${mm}/${thYear}`;
  })();

  return (
    <input
      readOnly
      onClick={onClick}
      ref={ref}
      value={displayValue}
      placeholder="เลือกวันเกิด"
      className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-300"
    />
  );
});

const formatDateToThai = (date) => {
  const formattedDate = format(date, "dd-MM-yyyy");
  const [dd, mm, yyyy] = formattedDate.split("-");
  return `${dd}-${mm}-${parseInt(yyyy) + 543}`;
};

export default function LuckyNameChecker() {
  const [name, setName] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleSubmit = async () => {
    if (!name || !selectedDate) {
      alert("กรุณากรอกชื่อและเลือกวันเกิด");
      return;
    }

    const birthDate = format(selectedDate, "yyyy-MM-dd");

    // ✅ GTM DataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_submit_lucky_name",
      category: "lucky_name",
      label: "lucky_name Form Submit",
      name: name,
      birth_date: birthDate,
    });

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BIGC_BIGLINE_API_URL}/horoscope/check-lucky-name`,
        { name, birthDate },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_BIGC_BIGLINE_API_KEY,
          },
        }
      );
      setResult(response.data);
      setModalOpen(true);

      // ✅ GTM DataLayer Event หลังได้ผลลัพธ์ลัคนา
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "click_view_lucky_name_result",
        name: name,
        birth_date: birthDate,
        nameNumber: response.data?.nameNumber,
        lifePathNumber: response.data?.lifePathNumber,
        birthdayNumber: response.data?.birthdayNumber,
        compatibilityScore: response.data?.compatibilityScore,
      });
      
    } catch (error) {
      console.error("API Error:", error);
      alert("เกิดข้อผิดพลาดในการเช็คชื่อ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 border rounded-lg w-full max-w-md sm:max-w-lg md:max-w-xl mx-auto mb-6 bg-white shadow">
      <h2 className="text-lg font-bold mb-4 text-center">🔢 ตรวจสอบชื่อมงคล</h2>

      {/* ✅ Name Input */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">ชื่อบริษัท / ชื่อบุคคล</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full h-[40px] px-3 py-2 border border-gray-300 rounded text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-purple-300"
          placeholder="ชื่อบริษัท / ชื่อบุคคล"
        />
      </div>

      {/* ✅ DatePicker */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">วันก่อตั้ง / วันเกิด</label>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          locale={th}
          dateFormat="dd-MM-yyyy"
          maxDate={new Date()}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          customInput={<CustomDateInput />}
          renderCustomHeader={({ date, changeYear, changeMonth }) => {
            const currentYear = date.getFullYear();
            const years = [];
            for (let y = 1950; y <= new Date().getFullYear(); y++)
              years.push(y);

            const months = [
              "มกราคม",
              "กุมภาพันธ์",
              "มีนาคม",
              "เมษายน",
              "พฤษภาคม",
              "มิถุนายน",
              "กรกฎาคม",
              "สิงหาคม",
              "กันยายน",
              "ตุลาคม",
              "พฤศจิกายน",
              "ธันวาคม",
            ];

            return (
              <div className="flex justify-between gap-2 px-2 py-1 items-center">
                <select
                  value={date.getMonth()}
                  onChange={({ target: { value } }) => changeMonth(value)}
                  className="px-2 py-1 border rounded w-[110px] text-sm"
                >
                  {months.map((month, idx) => (
                    <option key={month} value={idx}>
                      {month}
                    </option>
                  ))}
                </select>

                <select
                  value={currentYear}
                  onChange={({ target: { value } }) => changeYear(value)}
                  className="px-2 py-1 border rounded w-[100px] text-sm"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year + 543}
                    </option>
                  ))}
                </select>
              </div>
            );
          }}
        />
      </div>

      {/* ✅ Button */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full p-2 bg-green-600 text-white rounded hover:bg-green-700 transition text-sm sm:text-base"
      >
        {loading ? "⏳ กำลังคำนวณ..." : "🔍 เช็คชื่อมงคล"}
      </button>

      {/* ✅ Modal */}
      {modalOpen && result && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-xl p-6 relative overflow-y-auto max-h-[85vh]">
            <button
              className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-xl"
              onClick={() => setModalOpen(false)}
            >
              ❌
            </button>

            <h3 className="text-xl font-bold text-purple-700 mb-3">
              🔢 ผลลัพธ์ชื่อมงคล
            </h3>

            <div className="text-left space-y-3 text-sm sm:text-base">
              <p>
                <strong>ชื่อบริษัท / ชื่อบุคล :</strong> {name}
              </p>
              <p>
                <strong>วันก่อตั้ง / วันเกิด :</strong>{" "}
                {selectedDate && formatDateToThai(selectedDate)}
              </p>

              <div>
                <strong className="text-lg">
                  เลขศาสตร์ หมายเลข {result.nameNumber}:
                </strong>
                <p>
                  <strong>ภาพรวม:</strong> {result.nameMeaning?.ภาพรวม}
                </p>
                <p>
                  <strong>บุคคล:</strong> {result.nameMeaning?.บุคคล}
                </p>
                <p>
                  <strong>บริษัท:</strong> {result.nameMeaning?.บริษัท}
                </p>
              </div>

              <p>
                <strong>Life Path Number:</strong> {result.lifePathNumber}
              </p>
              <p>
                <strong>Birthday Number:</strong> {result.birthdayNumber}
              </p>
              <p>
                <strong>Compatibility:</strong> {result.compatibilityScore}%
              </p>
              {/*<p><strong>ชื่อมงคลไหม:</strong> {result.isLuckyName ? "✅ ใช่" : "❌ ไม่ใช่"}</p>*/}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
